import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  LogCategory,
  LogTemplateType,
  useKpiQaqcLogEntriesQuery,
  useAllQaqcLogsTemplatesForKpiTrackerQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { COLLECTION_NAMES } from '@/constants';
import * as kpiState from '../state';
import { formatDateToYYYYMMDD } from '../utils';
import { computeQaqcFieldCompletion } from '../QaQcPanel/utils';

export const useQaqcJobPhaseProgress = () => {
  const structureId = useGetStructureId();
  const workpackId = useAtomValue(kpiState.selectedWorkpackIdForKpiTracker);
  const selectedDate = useAtomValue(kpiState.selectedDate);
  const selectedShift = useAtomValue(kpiState.selectedShift);

  const formattedDate = useMemo(() => formatDateToYYYYMMDD(selectedDate), [selectedDate]);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const shouldSkip = !structureId || !workpackId;

  const { data: templateData, loading: templateLoading } =
    useAllQaqcLogsTemplatesForKpiTrackerQuery({
      variables: {
        input: {
          structureId: structureId ?? '',
          type: LogTemplateType.QaqcTemplate,
        },
      },
      skip: shouldSkip,
      fetchPolicy: 'cache-first',
    });

  const template = templateData?.allLogsTemplates?.at(0);

  const { data: logEntriesData, loading: logEntriesLoading } = useKpiQaqcLogEntriesQuery({
    variables: {
      input: {
        collectionName: COLLECTION_NAMES.WORKPACK,
        structureId: structureId ?? undefined,
        referenceId: workpackId,
        category: LogCategory.QaqcForm,
        logsTemplateId: template?.id,
        startDate: formattedDate,
        endDate: formattedDate,
        shift: selectedShift,
        timezone: userTimezone,
      },
    },
    skip: shouldSkip || !template?.id,
    fetchPolicy: 'cache-first',
  });

  const existingLog = useMemo(() => {
    const list = logEntriesData?.logEntries ?? [];
    if (!template?.id) {
      return undefined;
    }
    return list.find((entry) => entry.logsTemplate?.id === template.id) ?? list.at(0);
  }, [logEntriesData?.logEntries, template?.id]);

  const progress = useMemo(() => {
    return computeQaqcFieldCompletion(template, existingLog ?? null);
  }, [existingLog, template]);

  const hasSavedJobPhaseForm = useMemo(() => {
    return Boolean(existingLog?.sections?.length);
  }, [existingLog?.sections?.length]);

  return {
    loading: templateLoading || logEntriesLoading,
    template,
    progress,
    existingLog,
    hasSavedJobPhaseForm,
  };
};
